import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type Category = {
  labelKey: string;
  searchTerm: string;
  image: string;
};

const brand = {
  primary: "#F5A623",
  secondary: "#00AEEF",
  text: "#1e293b",
  surface: "#ffffff",
  // CHANGED: Set to WHITE to perfectly match JPEG backgrounds
  background: "#ffffff",
  borderNeutral: "#e2e8f0",
};

const categories: Category[] = [
  {
    labelKey: "products.categories.unlockedCellPhones",
    searchTerm: "unlocked iPhones",
    image: "/logos/top-categories/unlcoked_cell.webp",
  },
  {
    labelKey: "products.categories.headphones",
    searchTerm: "headphones",
    image: "/logos/top-categories/headphones.webp",
  },
  {
    labelKey: "products.categories.laptops",
    searchTerm: "laptop",
    image: "/logos/top-categories/laptops.webp",
  },
  {
    labelKey: "products.categories.macbooks",
    searchTerm: "macbook",
    image: "/logos/top-categories/macbooks.webp",
  },
  {
    labelKey: "products.categories.ipads",
    searchTerm: "ipad",
    image: "/logos/top-categories/ipads.webp",
  },
  {
    labelKey: "products.categories.computers",
    searchTerm: "computer",
    image: "/logos/top-categories/computers.webp",
  },
  {
    labelKey: "products.categories.gamingConsoles",
    searchTerm: "gaming console",
    image: "/logos/top-categories/gaming_consoles.webp",
  },
  {
    labelKey: "products.categories.tvs",
    searchTerm: "television",
    image: "/logos/top-categories/tvs.webp",
  },
  {
    labelKey: "products.categories.cameras",
    searchTerm: "camera",
    image: "/logos/top-categories/cameras.webp",
  },
];

const CARD_WIDTH = "clamp(120px, 22vw, 170px)";

const TopCategories: React.FC = () => {
  const { t: myTranslate } = useTranslation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const handleClick = (term: string) => {
    navigate(
      `/search?query=${encodeURIComponent(term)}&retailer=amazon&page=1`,
    );
  };

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", checkScrollPosition);
      checkScrollPosition();
    }
    return () => {
      if (container)
        container.removeEventListener("scroll", checkScrollPosition);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, []);

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.75;
      const delta = direction === "left" ? -scrollAmount : scrollAmount;
      scrollContainerRef.current.scrollBy({ left: delta, behavior: "smooth" });
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", lg: 1200 },
        mx: "auto",
        mb: 6,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          className="notranslate"
          translate="no"
          sx={{ fontWeight: 800, color: brand.text, letterSpacing: "-0.5px" }}
        >
          {myTranslate("products.popularCategories")}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            size="small"
            aria-label="Scroll categories left"
            sx={{
              bgcolor: "white",
              border: `1px solid ${brand.borderNeutral}`,
              opacity: canScrollLeft ? 1 : 0.3,
              "&:hover": { bgcolor: "white", borderColor: brand.text },
              cursor: canScrollLeft ? "pointer" : "default",
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            size="small"
            aria-label="Scroll categories right"
            sx={{
              bgcolor: "white",
              border: `1px solid ${brand.borderNeutral}`,
              opacity: canScrollRight ? 1 : 0.3,
              "&:hover": { bgcolor: "white", borderColor: brand.text },
              cursor: canScrollRight ? "pointer" : "default",
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          gap: { xs: 1.5, md: 2 },
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          overscrollBehaviorX: "contain",
          pt: 1,
          pb: 3,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {categories.map((category, index) => {
          const isLastItem = index === categories.length - 1;
          const snapAlign = isLastItem ? "end" : "start";

          return (
            <Box
              key={category.labelKey}
              component="button"
              type="button"
              onClick={() => handleClick(category.searchTerm)}
              aria-label={myTranslate(category.labelKey)}
              sx={{
                width: CARD_WIDTH,
                flex: `0 0 ${CARD_WIDTH}`,
                scrollSnapAlign: snapAlign,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                bgcolor: brand.surface,
                borderRadius: 3,
                border: `1px solid ${brand.borderNeutral}`,
                cursor: "pointer",
                overflow: "hidden",
                p: 0,
                appearance: "none",
                WebkitAppearance: "none",
                transition:
                  "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s, border-color 0.3s",
                "@media (hover: hover) and (pointer: fine)": {
                  "&:hover": {
                    transform: "translateY(-6px)",
                    borderColor: "transparent",
                    boxShadow: `
                      0 4px 6px -1px rgba(0, 0, 0, 0.05),
                      0 12px 20px -4px rgba(0, 0, 0, 0.1)
                    `,
                    "& .category-image": {
                      transform: "scale(1.08)",
                    },
                  },
                },
                "&:focus-visible": {
                  outline: `2px solid ${brand.text}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 120, md: 150 },
                  // BACKGROUND IS NOW WHITE (same as image)
                  bgcolor: brand.background,
                  // ADDED BORDER to define the box shape since bg is white
                  borderBottom: `1px solid ${brand.borderNeutral}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  position: "relative",
                  // 'isolate' prevents mix-blend-mode from bleeding too far up
                  isolation: "isolate",
                }}
              >
                <Box
                  component="img"
                  className="category-image"
                  src={category.image}
                  alt={myTranslate(category.labelKey)}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    transition:
                      "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",

                    // We keep 'multiply' just in case the image is "off-white"
                    // but since the background is white, this is now safe on mobile.
                    mixBlendMode: "multiply",
                    filter:
                      "contrast(1.02) brightness(1.02) drop-shadow(0 4px 6px rgba(0,0,0,0.04))",
                  }}
                />
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  width: "100%",
                  textAlign: "center",
                  minHeight: { xs: 36, md: 40 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  className="notranslate"
                  translate="no"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.8rem", md: "0.9rem" },
                    color: brand.text,
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {myTranslate(category.labelKey)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TopCategories;
